import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { EventModel } from '@app/events/shared/event.model';
import { provideFlatpickrDefaults } from 'angularx-flatpickr';
import { FormControl, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { PaymentModel } from '@app/events/shared/payment.model';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { PaymentService } from '@app/events/shared/payment.service';
import { TextareaComponent } from '@app/partials/form/textarea/textarea.component';
import { DatePickerComponent } from '@app/partials/form/date-picker/date-picker.component';
import { CheckboxComponent } from '@app/partials/form/checkbox/checkbox.component';
import { DateUtil } from '@app/shared/utils/date.util';

@Component({
  selector: 'app-event-payment-edit-form',
  templateUrl: './payment-edit-form.component.html',
  styleUrls: [],
  providers: [provideFlatpickrDefaults()],
  imports: [ButtonComponent, SelectComponent, TextInputComponent, TextareaComponent, DatePickerComponent, CheckboxComponent],
  standalone: true,
})
export class PaymentEditFormComponent implements OnInit {
  @Input() public event!: EventModel;
  @Input() public payment!: PaymentModel;

  @Output() paymentUpdated = new EventEmitter<PaymentModel>();
  @Output() formCancelled = new EventEmitter<void>();

  protected categoryChoices = [
    { value: 'Food', label: 'Food' },
    { value: 'Drinks', label: 'Drinks' },
    { value: 'Accommodation', label: 'Accommodation' },
    { value: 'Add-on', label: 'Add-on' },
    { value: 'Supplier', label: 'Supplier' },
    { value: 'Expense', label: 'Expense' },
    { value: 'Service', label: 'Service' },
    { value: 'Payment', label: 'Payment' },
  ];

  protected form: FormGroup<{
    amount: FormControl<string | null>;
    category: FormControl<string | null>;
    description: FormControl<string | null>;
    date_due: FormControl<string | null>;
    date_paid: FormControl<string | null>;
    amount_paid: FormControl<string | null>;
    is_reservation_fee: FormControl<boolean | null>;
  }>;

  constructor(private paymentService: PaymentService) {
    this.form = new FormGroup({
      amount: new FormControl('', [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)]),
      category: new FormControl('', [
        Validators.required,
        Validators.pattern(/^(Food|Drinks|Accommodation|Add-on|Supplier|Expense|Service|Payment)$/),
      ]),
      description: new FormControl('', [Validators.maxLength(1000)]),
      date_due: new FormControl(''),
      date_paid: new FormControl(''),
      amount_paid: new FormControl('0', [Validators.pattern(/^\d+(\.\d{1,2})?$/)]),
      is_reservation_fee: new FormControl(false),
    });
  }

  ngOnInit(): void {
    // Populate form with existing payment data
    this.form.patchValue({
      amount: this.payment.amount?.toString() || '',
      category: this.payment.category || '',
      description: this.payment.description || '',
      date_due: this.payment.date_due ? DateUtil.parseDateToString(this.payment.date_due) : null,
      date_paid: this.payment.date_paid ? DateUtil.parseDateToString(this.payment.date_paid) : null,
      amount_paid: this.payment.amount_paid?.toString() || '0',
      is_reservation_fee: this.payment.is_reservation_fee || false,
    });

    // Watch for amount_paid changes to auto-fill date_paid if empty
    this.form.get('amount_paid')?.valueChanges.subscribe(value => {
      if (value && parseFloat(value) > 0 && !this.form.get('date_paid')?.value) {
        // Auto-fill date_paid with today's date when amount_paid is entered
        const today = new Date().toISOString().split('T')[0];
        this.form.get('date_paid')?.setValue(today, { emitEvent: false });
      } else if (!value || parseFloat(value) === 0) {
        // Clear date_paid when amount_paid is cleared or set to 0
        this.form.get('date_paid')?.setValue(null, { emitEvent: false });
      }
    });

  }


  save() {
    if (!this.form || this.form.invalid) {
      return;
    }

    const data: PaymentModel = {
      ...this.payment,
      description: this.form.value.description ?? '',
      category: this.form.value.category as any,
      event_id: this.event.id,
      date_due: this.form.value.date_due ? new Date(this.form.value.date_due) : null,
      date_paid: this.form.value.date_paid ? new Date(this.form.value.date_paid) : null,
      amount_paid: this.form.value.amount_paid ? parseFloat(this.form.value.amount_paid) : 0,
      is_reservation_fee: this.form.value.is_reservation_fee ?? false,
    };

    // Only include amount if not system-generated (amount is auto-calculated for those)
    if (!this.isSystemGeneratedPayment) {
      data.amount = parseFloat(this.form.value.amount ?? '0');
    }

    this.paymentService.save(data).subscribe({
      next: (response: any) => {
        this.paymentUpdated.emit(response.data);
      },
      error: (error: any) => {
        console.error('Error updating payment:', error);
      },
    });
  }

  cancel() {
    this.formCancelled.emit();
  }

  fillFullAmount() {
    const amount = this.form.get('amount')?.value;
    if (amount) {
      this.form.get('amount_paid')?.setValue(amount);
      this.form.markAsDirty();
    }
  }

  fillHalfAmount() {
    const amount = this.form.get('amount')?.value;
    if (amount) {
      const halfAmount = (parseFloat(amount) / 2).toFixed(2);
      this.form.get('amount_paid')?.setValue(halfAmount);
      this.form.markAsDirty();
    }
  }

  fillToday() {
    const today = new Date().toISOString().split('T')[0];
    this.form.get('date_paid')?.setValue(today);
    this.form.markAsDirty();
  }

  clearAmountPaid() {
    this.form.get('amount_paid')?.setValue('0');
    this.form.markAsDirty();
  }

  clearDatePaid() {
    this.form.get('date_paid')?.setValue(null);
    this.form.markAsDirty();
  }

  get isSystemGeneratedPayment(): boolean {
    return this.payment?.is_system_generated ?? false;
  }
}